import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { AuthorizedRequest } from '../../../api/common.types';
import { requestChatAiSuggestion } from '../../../api/main/api';
import { ChatAiSuggestionOptions } from '../../../api/main/api.types';
import ModalConfirm from '../../primitive/modal.confirm';
import { CloseIcon } from '../../ui/icons';
import './ui.scss';

interface AiReplyAssistantProps {
  chatId: number | null;
  chatTitle: string;
  scope: 'chats' | 'wds/chats';
  performAuthorizedRequest: AuthorizedRequest;
  draft: string;
  disabled: boolean;
  onInsert: (text: string) => void;
}

const RESPONSE_MODES: Array<{ value: ChatAiSuggestionOptions['responseMode']; title: string; description: string }> = [
  { value: 'simple', title: 'Упрощенный', description: 'Короче и проще' },
  { value: 'medium', title: 'Средний', description: 'Спокойно и по делу' },
  { value: 'assertive', title: 'С напором', description: 'Уверенный следующий шаг' }
];

export function AiReplyAssistant({ chatId, chatTitle, scope, performAuthorizedRequest, draft, disabled, onInsert }: AiReplyAssistantProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [options, setOptions] = useState<ChatAiSuggestionOptions>({ responseMode: 'medium', ignoreAfterLastClient: true });
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [deferredSuggestion, setDeferredSuggestion] = useState('');
  const requestRef = useRef<{ controller: AbortController; target: string } | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(false);
  const disabledRef = useRef(disabled);
  const draftRef = useRef(draft);
  const insertRef = useRef(onInsert);
  const modalRootRef = useRef<HTMLDivElement | null>(null);
  const targetKey = `${scope}:${chatId}`;
  const targetKeyRef = useRef(targetKey);
  disabledRef.current = disabled;
  draftRef.current = draft;
  insertRef.current = onInsert;
  targetKeyRef.current = targetKey;

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    requestRef.current?.controller.abort();
    requestRef.current = null;
    setIsModalOpen(false);
    setPending(false);
    setNotice('');
    setError('');
    setDeferredSuggestion('');
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
      requestRef.current?.controller.abort();
      requestRef.current = null;
    };
  }, []);

  // Keep response preferences while cancelling all work belonging to the previous conversation.
  useEffect(() => reset(), [targetKey, reset]);

  useEffect(() => {
    if (disabled) {
      reset();
    }
  }, [disabled, reset]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const dialog = modalRootRef.current?.querySelector<HTMLElement>('[role="dialog"]');
    dialog?.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setIsModalOpen(false);
      }
      if (event.key === 'Tab') {
        const focusable = Array.from(dialog?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)') ?? []);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) {
        previousFocus.focus();
      }
    };
  }, [isModalOpen]);

  const generate = async () => {
    if (chatId === null || disabledRef.current || requestRef.current) {
      return;
    }
    const id = ++requestIdRef.current;
    const controller = new AbortController();
    const requestedTarget = targetKey;
    const originalDraft = draftRef.current;
    requestRef.current = { controller, target: requestedTarget };
    const isCurrentRequest = () => mountedRef.current && !disabledRef.current
      && requestIdRef.current === id && targetKeyRef.current === requestedTarget;
    setIsModalOpen(false);
    setPending(true);
    setNotice('AI готовит черновик...');
    setDeferredSuggestion('');
    setError('');

    try {
      const payload = await requestChatAiSuggestion(performAuthorizedRequest, chatId, scope, options, controller.signal);
      if (isCurrentRequest()) {
        if (draftRef.current === originalDraft) {
          insertRef.current(payload.text);
          setNotice('AI подготовил черновик. Проверьте текст перед отправкой.');
        } else {
          setDeferredSuggestion(payload.text);
          setNotice('AI подготовил черновик. Текст сообщения изменился во время подготовки.');
        }
      }
    } catch (caught) {
      if (isCurrentRequest()) {
        setNotice('');
        setError(caught instanceof Error ? caught.message : 'Не удалось подготовить ответ. Попробуйте ещё раз.');
      }
    } finally {
      if (isCurrentRequest()) {
        requestRef.current = null;
        setPending(false);
      }
    }
  };

  const currentPending = pending && requestRef.current?.target === targetKey;
  const dialogId = `ai-reply-${scope.replace('/', '-')}-${chatId}`;

  return (
    <div className="telegram-ai-assistant">
      <button
        className="telegram-composer-field__action telegram-composer-field__action--ai"
        type="button"
        onClick={() => {
          setNotice('');
          setError('');
          setDeferredSuggestion('');
          setIsModalOpen(true);
        }}
        disabled={disabled || chatId === null || currentPending}
        aria-label={currentPending ? 'AI изучает переписку' : 'Предложить ответ с помощью AI'}
        title="Предложить ответ на основе переписки"
        aria-haspopup="dialog"
        aria-expanded={isModalOpen}
        aria-controls={isModalOpen ? dialogId : undefined}
        aria-busy={currentPending}
      >
        {currentPending ? '...' : 'AI'}
      </button>

      {notice || error ? (
        <div className="telegram-ai-notice" role={error ? 'alert' : 'status'}>
          <span>{error || notice}</span>
          {deferredSuggestion ? (
            <button className="telegram-ai-notice__insert" type="button" disabled={disabled} onClick={() => {
              if (!disabledRef.current) {
                insertRef.current(deferredSuggestion);
                setDeferredSuggestion('');
                setNotice('AI подготовил черновик. Проверьте текст перед отправкой.');
              }
            }}>Заменить текст ответом AI</button>
          ) : null}
          {!currentPending ? <button className="telegram-ai-notice__close" type="button" onClick={() => {
            setNotice('');
            setError('');
            setDeferredSuggestion('');
          }} aria-label="Убрать уведомление AI"><CloseIcon width="16px" height="16px" /></button> : null}
        </div>
      ) : null}

      {isModalOpen ? createPortal(
        <div ref={modalRootRef} id={dialogId}>
          <ModalConfirm
            isOpen
            onClose={() => setIsModalOpen(false)}
            closeAriaLabel="Закрыть окно ответа AI"
            dialogAriaLabel="Ответ AI"
            containerClassName="telegram-ai-modal"
            dialogClassName="telegram-ai-dialog"
            showHeaderCloseButton
            title={<><strong>Ответ AI</strong><span>{chatTitle}</span></>}
            actions={<>
              <button className="telegram-confirm__button telegram-confirm__button--ghost" type="button" onClick={() => setIsModalOpen(false)}>Отмена</button>
              <button className="telegram-confirm__button telegram-confirm__button--danger" type="button" onClick={() => void generate()} disabled={disabled || currentPending}>Подготовить</button>
            </>}
          >
            <div className="telegram-ai-dialog__content">
              <fieldset className="telegram-ai-modes">
                <legend>Режим ответа</legend>
                <div className="telegram-ai-modes__options">
                  {RESPONSE_MODES.map((mode) => (
                    <label className="telegram-ai-mode" key={mode.value}>
                      <input type="radio" name={dialogId} value={mode.value} checked={options.responseMode === mode.value} onChange={() => setOptions((previous) => ({ ...previous, responseMode: mode.value }))} />
                      <span><strong>{mode.title}</strong><span>{mode.description}</span></span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="telegram-ai-last-client">
                <input type="checkbox" checked={options.ignoreAfterLastClient} onChange={(event) => setOptions((previous) => ({ ...previous, ignoreAfterLastClient: event.target.checked }))} />
                <span>Ответить на последнее сообщение клиента</span>
              </label>
            </div>
          </ModalConfirm>
        </div>, document.body
      ) : null}
    </div>
  );
}
